import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// fonts.css declares the @font-face rules that tokens.css then NAMES in
// --font-family / --font-family-numeric, so it goes first. tokens.css MUST in
// turn load before any component CSS, since every component stylesheet consumes
// its custom properties.
import "@/styles/fonts.css";
import "@/styles/tokens.css";
import "@/styles/global.css";
import "@/styles/ruled-rows.css"; // ef-056-0a — .ruled-rows utility, ADR 0010 §4/D3
import "@/styles/dialog-viewport.css"; // every aria-modal dialog sizes to the visual viewport (iOS keyboard)
import { installDialogViewport } from "@/lib/dialogViewport";

installDialogViewport();

const container = document.getElementById("root");
if (!container) {
  throw new Error("#root element not found — check web/index.html.");
}

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);

/**
 * of-001 PWA shell: register the service worker that precaches the app
 * shell, so a RELOAD while offline renders the app instead of the browser's
 * offline page (docs/demo/tradeshow-2026-09.md "Operator journey — the
 * offline moment"). Deferred to `load` so it never competes with the first
 * paint for bandwidth — this is a progressive enhancement, not something the
 * first visit depends on (the QR flow already guarantees that visit is
 * online).
 *
 * Registration failure degrades silently to "no offline reload" — logged,
 * never thrown, and never shown to the visitor. It must not be able to
 * break the online path, which is the one that is never allowed to fail.
 */
/**
 * THE OFF SWITCH, because a precached shell and a person testing a change are natural
 * enemies.
 *
 * `?sw=off` unregisters the worker, deletes its caches, and REMEMBERS the choice in
 * localStorage so every later load on this browser skips registration. `?sw=on` puts it
 * back. Nothing else on the page reads this.
 *
 * WHY REMEMBERED RATHER THAN PER-LOAD. A one-shot query param is worse than useless here:
 * you would strip it the moment you clicked a link, the worker would re-register, and the
 * next reload would serve the shell again — from a page that looked like it had the switch
 * off. The flag has to outlive the URL that set it.
 *
 * WHY IT IS SAFE IN PRODUCTION. It is opt-in per browser, needs a deliberate URL, and the
 * only thing it costs is offline reload for whoever typed it. The airplane-mode beat is
 * unaffected for everyone else, and `deploy/verify-offline-shell.sh` never sets it — so the
 * gate still proves the shell precaches on every deploy.
 */
const SW_FLAG = "easyfarm:sw";

async function disableServiceWorker(): Promise<void> {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
    // Unregistering stops the worker; it does NOT drop what it already stored, and a
    // cache-first asset handler would keep answering from those entries on the next load.
    //
    // ⚠️ DELETED UNTIL IT STAYS DELETED, and one pass is genuinely not enough. sw.js's
    // `install` handler calls `caches.open(CACHE_NAME)` — which CREATES the cache — and
    // then awaits a set of fetches inside `event.waitUntil`. `unregister()` resolves
    // without stopping a worker that is mid-install, so those `cache.put`s land AFTER a
    // single delete and the cache is simply back. The switch then reports itself off while
    // a cache-first handler still has the old assets to answer from, which is the exact
    // failure this switch exists to prevent: you would conclude the deploy was broken
    // rather than the switch.
    //
    // MEASURED 2026-09-09, not inferred: `offline.spec.ts`'s own "?sw=off turns the service
    // worker off" test failed 4 of 8 consecutive runs against the previous single-pass
    // version, always on `caches.keys()` returning exactly 1. It is a race, so it read as
    // flake — which is worse than a clean failure, because a gate that is a coin flip stops
    // being read.
    //
    // Two consecutive empty reads, not one: a cache recreated 50ms after a single empty
    // check would sail through. The install fetches a bounded handful of URLs, so this
    // converges in well under the ceiling; the ceiling exists so a pathological worker
    // cannot hang a page load, and this path is only ever reached by someone who typed
    // ?sw=off on purpose.
    if ("caches" in window) {
      let settled = 0;
      for (let attempt = 0; attempt < 20 && settled < 2; attempt++) {
        const names = await caches.keys();
        if (names.length === 0) {
          settled += 1;
        } else {
          settled = 0;
          await Promise.all(names.map((n) => caches.delete(n)));
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }
    console.info("[easyfarm] service worker off — reload to see changes immediately.");
  } catch (err) {
    console.warn("[easyfarm] could not fully disable the service worker.", err);
  }
}

/**
 * ⚠️ EVERY localStorage ACCESS IS GUARDED, and this is not defensive habit — it is a bug
 * this file already shipped once today and the suite caught.
 *
 * In private browsing, and anywhere a browser is set to block site data, localStorage
 * THROWS on access rather than returning null. `quick-entry.spec.ts` simulates exactly that
 * ("a visitor with storage blocked — private browsing — can still finish an entry") because
 * it is the visitor path: a stranger scans the QR on their own phone, and plenty of phones
 * arrive in private mode. Unguarded, this ran at module load and killed the whole app before
 * first paint — three projects red on `pageerror: simulated private-browsing storage
 * failure`.
 *
 * Storage being unavailable means the switch cannot be remembered. That is fine, and the
 * fallback is the safe one: the worker registers as it always has.
 */
function swFlag(): string | null {
  try {
    return localStorage.getItem(SW_FLAG);
  } catch {
    return null;
  }
}

function setSwFlag(value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(SW_FLAG);
    else localStorage.setItem(SW_FLAG, value);
  } catch {
    // No storage: the choice lasts for this page load only, which is the honest outcome.
  }
}

if ("serviceWorker" in navigator) {
  const requested = new URLSearchParams(window.location.search).get("sw");
  if (requested === "off") setSwFlag("off");
  if (requested === "on") setSwFlag(null);

  if (requested === "off" || swFlag() === "off") {
    void disableServiceWorker();
  } else {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch((err: unknown) => {
        console.warn("Service worker registration failed; offline reload will not work.", err);
      });
    });
  }
}
